import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useSearchParams } from 'react-router-dom';
import {
    Plus,
    Calendar,
    Clock,
    CheckCircle,
    User,
    ArrowLeft,
    X,
    FileText,
    Upload,
    Send,
    Loader2,
    Paperclip,
    ChevronRight,
    Printer,
    ShieldCheck,
    AlertTriangle,
    Settings2,
    Search,
    Trash2,
    Save,
    Pencil,
    UserPlus,
} from 'lucide-react';
import { FaMagic } from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';
import axiosInstance from '../utils/axiosConfig';
import { normalizarTexto } from '../utils/texto';
import toast from 'react-hot-toast';
import { Modal } from './common';
import {
    EstadoBadge,
    EmptyState,
    TabButton,
    RechazoModal,
    CalendarioGH,
    formatFecha,
    formatFechaDia,
    formatFechaLarga,
    formatHora,
    formatDias,
    withAuthToken,
    CATEGORIAS_PERMISO,
    CATEGORIA_COLORES,
    FiltrosGH,
    filtrarGH,
    descargarBlob,
    AvisoSolapamiento,
    AvisosReglas,
    ContadorPermisos,
} from './gh';

// Solo GH administra este módulo; admin, TI y captación lo ven como cualquier trabajador
const ROLES_GH = ['gh'];

const ESTADOS_PERMISO = [
    { value: 'pendiente', label: 'Pendiente' },
    { value: 'aprobado', label: 'Aprobado' },
    { value: 'rechazado', label: 'Rechazado' },
    { value: 'cancelado', label: 'Cancelado' },
];

const FORM_VACIO = {
    tipo: '',
    tipo_otro: '',
    compensa_fecha: '',
    modalidad: 'dia_completo',
    forma: 'un_dia',   // horas | un_dia | varios_dias (las dos últimas van como dia_completo)
    fecha_inicio: '',
    fecha_fin: '',
    fecha: '',
    hora_inicio: '',
    hora_fin: '',
    aprobador_id: '',
    motivo: '',
};

const TIPO_VACIO = {
    nombre: '',
    descripcion: '',
    categoria: 'programado',
    permite_horas: true,
    permite_dias: true,
    tope_dias: '',
    dias_continuos: false,
    remunerado: true,
    requiere_soporte: false,
    soporte: '',
    cuenta_tope_mensual: true,
    aprueba: 'jefe',
    activo: true,
};

// Horas de la jornada cada 15 minutos, de 6:00 a. m. a 7:00 p. m.
const OPCIONES_HORA = (() => {
    const lista = [];
    for (let m = 6 * 60; m <= 19 * 60; m += 15) {
        lista.push(`${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`);
    }
    return lista;
})();

// Barra de color de cada fila, por categoría del tipo
const BARRA_CATEGORIA = {
    ley: 'bg-sky-400',
    programado: 'bg-emerald-400',
    no_programado: 'bg-amber-400',
    compensatorio: 'bg-violet-400',
    otro: 'bg-gray-300',
};

const inputClass = 'w-full px-3 py-2.5 bg-white border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-sura-blue focus:border-sura-blue outline-none';
const botonPrimario = 'px-5 py-2.5 bg-sura-blue text-white rounded-xl text-sm font-medium hover:bg-sura-blue-dark transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sura-blue focus-visible:ring-offset-2';
const botonSecundario = 'px-4 py-2.5 border border-gray-200 bg-white text-gray-700 rounded-xl text-sm font-medium hover:bg-gray-50 transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sura-blue focus-visible:ring-offset-2';

// Duración de un rango de horas ("08:00", "11:00") -> "3 horas". Null si el rango no es válido.
const calcularDuracionHoras = (horaInicio, horaFin) => {
    if (!horaInicio || !horaFin) return null;
    const [h1, m1] = horaInicio.split(':').map(Number);
    const [h2, m2] = horaFin.split(':').map(Number);
    if ([h1, m1, h2, m2].some(Number.isNaN)) return null;
    const minutos = (h2 * 60 + m2) - (h1 * 60 + m1);
    if (minutos <= 0) return null;
    const horas = Math.floor(minutos / 60);
    const mins = minutos % 60;
    if (horas === 0) return `${mins} min`;
    if (mins === 0) return `${horas} ${horas === 1 ? 'hora' : 'horas'}`;
    return `${horas} h ${mins} min`;
};

// La regla de un tipo en pocas palabras: "Hasta 5 días hábiles", "Medio día", "Pide acta de matrimonio"...
// Si se paga o no lo decide el jefe al aprobar: al trabajador no se le muestra (conPago solo en Reglas).
const reglasDelTipo = (t, conPago = false) => {
    const partes = [];
    if (t.categoria === 'otro') {
        if (conPago) partes.push(t.remunerado ? 'Remunerado por defecto' : 'No remunerado por defecto');
        return partes;
    }
    if (t.tope_dias === null || t.tope_dias === undefined) {
        partes.push(t.categoria === 'ley' ? 'Lo que diga la ley' : 'Sin tope');
    } else if (t.tope_dias < 1) {
        partes.push('Medio día, por horas');
    } else {
        const unidad = t.dias_continuos ? 'continuos' : (t.tope_dias > 1 ? 'hábiles' : '');
        partes.push(`Hasta ${formatDias(t.tope_dias)}${unidad ? ` ${unidad}` : ''}`);
    }
    if (conPago && !t.remunerado) partes.push('No remunerado por defecto');
    if (t.requiere_soporte) partes.push(`Soporte: ${(t.soporte || 'documento').toLowerCase()}`);
    if (t.aprueba === 'gh') partes.push('Lo aprueba GH');
    return partes;
};

const cuandoTexto = (s) => {
    if (s.modalidad === 'dia_completo') {
        if (s.fecha_fin && s.fecha_fin !== s.fecha_inicio) {
            return `Del ${formatFecha(s.fecha_inicio)} al ${formatFecha(s.fecha_fin)}`;
        }
        return formatFecha(s.fecha_inicio);
    }
    return `${formatFecha(s.fecha)}, de ${formatHora(s.hora_inicio)} a ${formatHora(s.hora_fin)}`;
};

const diasTexto = (s, tipo) => {
    if (s.modalidad !== 'dia_completo') return calcularDuracionHoras(s.hora_inicio, s.hora_fin);
    if (!s.dias) return null;
    const unidad = tipo?.dias_continuos ? 'continuos' : 'hábiles';
    return `${formatDias(s.dias)} ${unidad}`;
};

// Cómo se puede pedir un tipo: unas horas, un día o varios días (según lo que permita y su tope)
const formasDelTipo = (t) => {
    if (!t) return [];
    const formas = [];
    if (t.permite_horas) formas.push(['horas', 'Unas horas']);
    if (t.permite_dias) {
        formas.push(['un_dia', 'Un día']);
        if (t.tope_dias === null || t.tope_dias === undefined || t.tope_dias > 1) formas.push(['varios_dias', 'Varios días']);
    }
    return formas;
};

const formaInicial = (t) => {
    if (!t.permite_dias) return 'horas';
    if (t.tope_dias === null || t.tope_dias === undefined || t.tope_dias > 1) return 'varios_dias';
    return 'un_dia';
};

const modalidadDe = (forma) => (forma === 'horas' ? 'rango_horas' : 'dia_completo');

// Selector de hora que se lee en a. m./p. m. y guarda 'HH:MM'
const HoraSelect = ({ id, value, onChange, desde }) => (
    <select id={id} value={value} onChange={(e) => onChange(e.target.value)} className={inputClass}>
        <option value="">Elige la hora</option>
        {OPCIONES_HORA.filter((h) => !desde || h > desde).map((h) => (
            <option key={h} value={h}>{formatHora(h)}</option>
        ))}
    </select>
);

const Casilla = ({ checked, onChange, label, hint }) => (
    <label className="flex items-start gap-2.5 text-sm text-gray-700 cursor-pointer">
        <input
            type="checkbox"
            checked={!!checked}
            onChange={(e) => onChange(e.target.checked)}
            className="mt-0.5 h-4 w-4 rounded border-gray-300 text-sura-blue focus:ring-sura-blue"
        />
        <span>
            {label}
            {hint && <span className="block text-xs text-gray-400">{hint}</span>}
        </span>
    </label>
);

const PermisosView = () => {
    const { user } = useAuth();
    const isGH = ROLES_GH.includes(user?.role);

    const [viewMode, setViewMode] = useState('list');
    const [activeTab, setActiveTab] = useState('mis');
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);

    const [misSolicitudes, setMisSolicitudes] = useState([]);
    const [porAprobar, setPorAprobar] = useState([]);
    const [porAprobarGH, setPorAprobarGH] = useState([]);
    const [todasSolicitudes, setTodasSolicitudes] = useState([]);
    const [selectedSolicitud, setSelectedSolicitud] = useState(null);

    const [tipos, setTipos] = useState([]);
    const [tiposTodos, setTiposTodos] = useState([]);
    const [reglas, setReglas] = useState(null);
    const [aprobacion, setAprobacion] = useState(null);

    // Rechazo
    const [showRechazoModal, setShowRechazoModal] = useState(false);
    const [motivoRechazo, setMotivoRechazo] = useState('');
    const [solicitudParaRechazar, setSolicitudParaRechazar] = useState(null);

    // Formulario
    const [formData, setFormData] = useState(FORM_VACIO);
    const [mesActual, setMesActual] = useState(null);
    const [compensables, setCompensables] = useState(null);   // días libres trabajados (Asistencia)
    const [searchParams, setSearchParams] = useSearchParams();
    const [soportes, setSoportes] = useState([]);
    const [paraOtro, setParaOtro] = useState(false);
    const [empleadoOtro, setEmpleadoOtro] = useState(null);
    const [aprobacionOtro, setAprobacionOtro] = useState(null);
    const [calculo, setCalculo] = useState({ dias: null, errores: [], avisos: [] });
    const [calculando, setCalculando] = useState(false);

    // Aprobación (uso del supervisor)
    const [remuneradoDecision, setRemuneradoDecision] = useState(true);
    const [subiendoSoporte, setSubiendoSoporte] = useState(false);

    // Calendario
    const [calYear, setCalYear] = useState(new Date().getFullYear());
    const [calMonth, setCalMonth] = useState(new Date().getMonth() + 1);
    const [calEventos, setCalEventos] = useState([]);
    const [calDepartamentoId, setCalDepartamentoId] = useState('');
    const [departamentos, setDepartamentos] = useState([]);

    // IA
    const [analisisIA, setAnalisisIA] = useState(null);
    const [analizandoIA, setAnalizandoIA] = useState(false);

    // Filtros del panel "Todas" (GH)
    const [fQ, setFQ] = useState('');
    const [fEstado, setFEstado] = useState('');
    const [fDepto, setFDepto] = useState('');
    const [fDesde, setFDesde] = useState('');
    const [fHasta, setFHasta] = useState('');
    const [exportando, setExportando] = useState(false);

    const tiposPorClave = useMemo(() => {
        const map = {};
        [...tiposTodos, ...tipos].forEach((t) => { map[t.clave] = t; });
        return map;
    }, [tipos, tiposTodos]);

    const tipoElegido = tiposPorClave[formData.tipo];

    // ========================
    // Carga de datos
    // ========================
    const fetchPermisos = useCallback(async () => {
        try {
            const res = await axiosInstance.get('/api/permisos/');
            setMisSolicitudes(res.data.mis_solicitudes || []);
            setPorAprobar(res.data.por_aprobar || []);
            setPorAprobarGH(res.data.por_aprobar_gh || []);
            setMesActual(res.data.mes_actual || null);
        } catch (err) {
            console.error('Error cargando permisos:', err);
        } finally {
            setLoading(false);
        }
    }, []);

    const fetchCatalogo = useCallback(async () => {
        try {
            const [resTipos, resReglas] = await Promise.all([
                axiosInstance.get('/api/permisos/tipos'),
                axiosInstance.get('/api/permisos/reglas'),
            ]);
            setTipos(resTipos.data);
            setReglas(resReglas.data);
            if (isGH) {
                const resTodos = await axiosInstance.get('/api/permisos/tipos', { params: { todos: 1 } });
                setTiposTodos(resTodos.data);
            }
        } catch (err) {
            console.error('Error cargando el catálogo de permisos:', err);
        }
    }, [isGH]);

    const fetchAprobacion = useCallback(async () => {
        if (!user?.empleado_id) return;
        try {
            const res = await axiosInstance.get('/api/permisos/aprobacion');
            setAprobacion(res.data);
        } catch (err) {
            console.error('Error cargando quién aprueba:', err);
        }
    }, [user?.empleado_id]);

    const fetchTodas = useCallback(async () => {
        if (!isGH) return;
        try {
            const res = await axiosInstance.get('/api/permisos/todas');
            setTodasSolicitudes(res.data);
        } catch (err) {
            console.error('Error cargando todas las solicitudes:', err);
        }
    }, [isGH]);

    const fetchCalendario = useCallback(async () => {
        try {
            const params = { year: calYear, month: calMonth };
            if (calDepartamentoId) params.departamento_id = calDepartamentoId;
            const res = await axiosInstance.get('/api/permisos/calendario', { params });
            setCalEventos(res.data.eventos);
        } catch (err) {
            console.error('Error cargando calendario:', err);
        }
    }, [calYear, calMonth, calDepartamentoId]);

    const fetchDepartamentos = useCallback(async () => {
        try {
            const res = await axiosInstance.get('/api/departamentos');
            setDepartamentos(res.data);
        } catch (err) {
            console.error('Error cargando departamentos:', err);
        }
    }, []);

    useEffect(() => {
        fetchPermisos();
        fetchCatalogo();
        fetchAprobacion();
    }, [fetchPermisos, fetchCatalogo, fetchAprobacion]);

    useEffect(() => {
        if (activeTab === 'todas') fetchTodas();
    }, [activeTab, fetchTodas]);

    useEffect(() => {
        if (activeTab === 'calendario') {
            fetchCalendario();
            if (departamentos.length === 0) fetchDepartamentos();
        }
        if (activeTab === 'todas' && departamentos.length === 0) fetchDepartamentos();
    }, [activeTab, fetchCalendario, fetchDepartamentos, departamentos.length]);

    // Quién aprueba cuando GH carga el permiso de otro trabajador
    useEffect(() => {
        if (!paraOtro || !empleadoOtro) { setAprobacionOtro(null); return; }
        let activo = true;
        axiosInstance.get('/api/permisos/aprobacion', { params: { empleado_id: empleadoOtro.empleado_id } })
            .then((res) => { if (activo) setAprobacionOtro(res.data); })
            .catch(() => { if (activo) setAprobacionOtro(null); });
        return () => { activo = false; };
    }, [paraOtro, empleadoOtro]);

    // Vista previa: días que cuenta y lo que dicen las reglas (con pausa para no llamar en cada tecla)
    useEffect(() => {
        if (viewMode !== 'create' || !formData.tipo) {
            setCalculo({ dias: null, errores: [], avisos: [] });
            return undefined;
        }
        const modalidad = modalidadDe(formData.forma);
        const listo = modalidad === 'dia_completo'
            ? !!formData.fecha_inicio
            : !!(formData.fecha && formData.hora_inicio && formData.hora_fin);
        if (!listo) {
            setCalculo({ dias: null, errores: [], avisos: [] });
            return undefined;
        }
        let activo = true;
        setCalculando(true);
        const timer = setTimeout(async () => {
            try {
                const payload = {
                    tipo: formData.tipo,
                    modalidad,
                    ...(modalidad === 'dia_completo'
                        ? { fecha_inicio: formData.fecha_inicio, fecha_fin: formData.forma === 'un_dia' ? formData.fecha_inicio : (formData.fecha_fin || formData.fecha_inicio) }
                        : { fecha: formData.fecha, hora_inicio: formData.hora_inicio, hora_fin: formData.hora_fin }),
                };
                if (paraOtro && empleadoOtro) payload.empleado_id = empleadoOtro.empleado_id;
                if (formData.compensa_fecha) payload.compensa_fecha = formData.compensa_fecha;
                const res = await axiosInstance.post('/api/permisos/calcular', payload);
                if (activo) setCalculo({ dias: res.data.dias, errores: res.data.errores || [], avisos: res.data.avisos || [] });
            } catch {
                if (activo) setCalculo({ dias: null, errores: [], avisos: [] });
            } finally {
                if (activo) setCalculando(false);
            }
        }, 400);
        return () => { activo = false; clearTimeout(timer); };
    }, [viewMode, formData.tipo, formData.forma, formData.fecha_inicio, formData.fecha_fin, formData.fecha,
        formData.hora_inicio, formData.hora_fin, formData.compensa_fecha, paraOtro, empleadoOtro]);

    // Tope del mes (normativa): permisos remunerados + ausencias sin justificar de Asistencia
    const usadosMes = (mesActual?.permisos || 0) + (mesActual?.ausencias || 0);

    // Descanso compensatorio: qué días libres trabajados (según Asistencia) puede compensar
    const tipoActual = tiposPorClave[formData.tipo];
    const diaPermiso = formData.forma === 'horas' ? formData.fecha : formData.fecha_inicio;
    useEffect(() => {
        if (viewMode !== 'create' || tipoActual?.categoria !== 'compensatorio' || !diaPermiso) {
            setCompensables(null);
            return undefined;
        }
        let activo = true;
        const params = { fecha: diaPermiso, modalidad: modalidadDe(formData.forma) };
        if (paraOtro && empleadoOtro) params.empleado_id = empleadoOtro.empleado_id;
        axiosInstance.get('/api/permisos/compensatorio', { params })
            .then((res) => { if (activo) setCompensables(res.data); })
            .catch(() => { if (activo) setCompensables(null); });
        return () => { activo = false; };
    }, [viewMode, tipoActual, diaPermiso, formData.forma, paraOtro, empleadoOtro]);

    // Justificar desde Asistencia: ?cargar=<empleado>&fecha=&forma=&desde=&hasta= abre "Cargar
    // permiso" con la persona y el día (o las horas) ya puestos
    useEffect(() => {
        const cargar = searchParams.get('cargar');
        if (!isGH || !cargar) return;
        const fecha = searchParams.get('fecha') || '';
        const forma = searchParams.get('forma') === 'horas' ? 'horas' : 'un_dia';
        const desde = searchParams.get('desde') || '';
        const hasta = searchParams.get('hasta') || '';
        setSearchParams({}, { replace: true });
        axiosInstance.get('/api/vacaciones/saldo', { params: { empleado_id: cargar } })
            .then((res) => {
                const e = res.data.empleado;
                abrirFormulario(true);
                setEmpleadoOtro({ empleado_id: e.id, nombre: e.nombre, ficha: e.ficha, cargo: e.cargo });
                setFormData((prev) => ({
                    ...prev,
                    forma,
                    modalidad: modalidadDe(forma),
                    ...(forma === 'horas'
                        ? { fecha, hora_inicio: desde, hora_fin: hasta }
                        : { fecha_inicio: fecha, fecha_fin: fecha }),
                }));
            })
            .catch(() => toast.error('No se pudo abrir el permiso de esa persona'));
    // Solo al llegar desde Asistencia
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isGH, searchParams]);

    const calEventosPorDia = useMemo(() => {
        const map = {};
        calEventos.forEach((ev) => {
            if (ev.modalidad === 'dia_completo') {
                if (!ev.fecha_inicio || !ev.fecha_fin) return;
                const inicio = new Date(`${ev.fecha_inicio}T00:00:00`);
                const fin = new Date(`${ev.fecha_fin}T00:00:00`);
                for (let d = new Date(inicio); d <= fin; d.setDate(d.getDate() + 1)) {
                    if (d.getMonth() + 1 === calMonth && d.getFullYear() === calYear) {
                        const dia = d.getDate();
                        (map[dia] = map[dia] || []).push(ev);
                    }
                }
            } else if (ev.fecha) {
                const f = new Date(`${ev.fecha}T00:00:00`);
                if (f.getMonth() + 1 === calMonth && f.getFullYear() === calYear) {
                    const dia = f.getDate();
                    (map[dia] = map[dia] || []).push(ev);
                }
            }
        });
        return map;
    }, [calEventos, calYear, calMonth]);

    const todasFiltradas = filtrarGH(
        todasSolicitudes,
        { q: fQ, estado: fEstado, departamento: fDepto, desde: fDesde, hasta: fHasta },
        { nombre: (s) => s.empleado_nombre, estado: (s) => s.estado, departamento: (s) => s.empleado_departamento, fecha: (s) => s.created_at },
    );

    // ========================
    // Acciones
    // ========================
    const handleExport = async () => {
        if (todasFiltradas.length === 0) { toast.error('No hay nada que exportar'); return; }
        try {
            setExportando(true);
            await descargarBlob(`/api/permisos/export?ids=${todasFiltradas.map((s) => s.id).join(',')}`, 'permisos.xlsx');
        } catch {
            toast.error('No se pudo exportar');
        } finally {
            setExportando(false);
        }
    };

    const handleAnalisisIA = async () => {
        try {
            setAnalizandoIA(true);
            const res = await axiosInstance.get('/api/permisos/analisis-patrones');
            setAnalisisIA(res.data);
        } catch (err) {
            toast.error(err.response?.data?.error || 'No se pudo generar el análisis');
        } finally {
            setAnalizandoIA(false);
        }
    };

    const abrirFormulario = (otro = false) => {
        setFormData(FORM_VACIO);
        setSoportes([]);
        setParaOtro(otro);
        setEmpleadoOtro(null);
        setCalculo({ dias: null, errores: [], avisos: [] });
        setViewMode('create');
    };

    const elegirTipo = (t) => {
        setFormData((prev) => {
            const yaHayFecha = prev.forma === 'horas' ? !!prev.fecha : !!prev.fecha_inicio;
            const admite = formasDelTipo(t).some(([f]) => f === prev.forma);
            if (yaHayFecha && admite) return { ...prev, tipo: t.clave, compensa_fecha: '' };
            const forma = formaInicial(t);
            return { ...prev, tipo: t.clave, forma, modalidad: modalidadDe(forma), fecha_fin: '', compensa_fecha: '' };
        });
    };

    const elegirForma = (forma) => {
        setFormData((prev) => ({ ...prev, forma, modalidad: modalidadDe(forma), fecha_fin: '' }));
    };

    const subirArchivos = async (permisoId, archivos) => {
        let fallidos = 0;
        for (const file of archivos) {
            try {
                const fd = new FormData();
                fd.append('archivo', file);
                await axiosInstance.post(`/api/permisos/${permisoId}/adjuntos`, fd);
            } catch {
                fallidos += 1;
            }
        }
        return fallidos;
    };

    const handleCrear = async () => {
        try {
            setSubmitting(true);
            const modalidad = modalidadDe(formData.forma);
            const payload = {
                tipo: formData.tipo,
                tipo_otro: formData.tipo_otro,
                compensa_fecha: formData.compensa_fecha || undefined,
                modalidad,
                motivo: formData.motivo,
                ...(modalidad === 'dia_completo'
                    ? { fecha_inicio: formData.fecha_inicio, fecha_fin: formData.forma === 'un_dia' ? formData.fecha_inicio : (formData.fecha_fin || formData.fecha_inicio) }
                    : { fecha: formData.fecha, hora_inicio: formData.hora_inicio, hora_fin: formData.hora_fin }),
            };
            if (formData.aprobador_id) payload.aprobador_id = Number(formData.aprobador_id);
            if (paraOtro && empleadoOtro) payload.empleado_id = empleadoOtro.empleado_id;
            const res = await axiosInstance.post('/api/permisos/', payload);
            const fallidos = await subirArchivos(res.data.id, soportes);
            if (fallidos > 0) {
                toast.error(`El permiso quedó pedido, pero ${fallidos} archivo(s) no se pudieron subir. Súbelos desde el detalle.`);
            } else if (paraOtro) {
                toast.success('Permiso cargado y aprobado');
            } else {
                toast.success(res.data.aprueba_gh ? 'Permiso enviado a Gestión Humana' : 'Permiso enviado a tu jefe');
            }
            setViewMode('list');
            setActiveTab(paraOtro ? 'todas' : 'mis');
            fetchPermisos();
            if (paraOtro) fetchTodas();
        } catch (err) {
            toast.error(err.response?.data?.error || 'No se pudo pedir el permiso');
        } finally {
            setSubmitting(false);
        }
    };

    const recargarDetalle = async (id) => {
        const res = await axiosInstance.get(`/api/permisos/${id}`);
        setSelectedSolicitud(res.data);
        setRemuneradoDecision(res.data.remunerado);
    };

    const handleCancelar = async (s) => {
        const anular = s.estado === 'aprobado';
        if (!window.confirm(anular ? '¿Anular este permiso ya aprobado?' : '¿Cancelar esta solicitud?')) return;
        try {
            await axiosInstance.delete(`/api/permisos/${s.id}`);
            toast.success(anular ? 'Permiso anulado' : 'Solicitud cancelada');
            fetchPermisos();
            if (isGH) fetchTodas();
            setViewMode('list');
        } catch (err) {
            toast.error(err.response?.data?.error || 'No se pudo cancelar');
        }
    };

    const handleAprobar = async (s) => {
        try {
            setSubmitting(true);
            await axiosInstance.post(`/api/permisos/${s.id}/aprobar`, { remunerado: remuneradoDecision });
            toast.success(remuneradoDecision ? 'Permiso aprobado' : 'Permiso aprobado como no remunerado');
            fetchPermisos();
            if (isGH) fetchTodas();
            await recargarDetalle(s.id);
        } catch (err) {
            toast.error(err.response?.data?.error || 'No se pudo aprobar');
        } finally {
            setSubmitting(false);
        }
    };

    const handleRechazar = async () => {
        if (!solicitudParaRechazar || !motivoRechazo.trim()) return;
        try {
            setSubmitting(true);
            await axiosInstance.post(`/api/permisos/${solicitudParaRechazar.id}/rechazar`, { motivo_rechazo: motivoRechazo });
            toast.success('Permiso rechazado');
            setShowRechazoModal(false);
            setMotivoRechazo('');
            setSolicitudParaRechazar(null);
            fetchPermisos();
            if (isGH) fetchTodas();
            if (viewMode === 'detail') setViewMode('list');
        } catch (err) {
            toast.error(err.response?.data?.error || 'No se pudo rechazar');
        } finally {
            setSubmitting(false);
        }
    };

    const handleSubirSoporte = async (s, archivos) => {
        if (!archivos.length) return;
        try {
            setSubiendoSoporte(true);
            const fallidos = await subirArchivos(s.id, archivos);
            if (fallidos > 0) toast.error(`${fallidos} archivo(s) no se pudieron subir. Solo PDF o imágenes de hasta 10 MB.`);
            else toast.success('Soporte subido');
            await recargarDetalle(s.id);
            fetchPermisos();
        } finally {
            setSubiendoSoporte(false);
        }
    };

    const handleEliminarAdjunto = async (s, adj) => {
        if (!window.confirm(`¿Quitar ${adj.nombre_original}?`)) return;
        try {
            await axiosInstance.delete(`/api/permisos/${s.id}/adjuntos/${adj.id}`);
            await recargarDetalle(s.id);
        } catch (err) {
            toast.error(err.response?.data?.error || 'No se pudo quitar el archivo');
        }
    };

    const openDetail = async (solicitud) => {
        try {
            await recargarDetalle(solicitud.id);
            setViewMode('detail');
        } catch {
            toast.error('No se pudo abrir el permiso');
        }
    };

    // ========================
    // RENDER: Lista
    // ========================
    const tope = reglas?.tope_mensual || 0;
    const renderList = () => (
        <div className="space-y-6">
            {user?.empleado_id && tope > 0 && (
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-3 rounded-2xl border border-gray-200 bg-white text-sm">
                    <span className="flex items-center gap-2 text-gray-700">
                        <span className="flex gap-1" aria-hidden="true">
                            {Array.from({ length: tope }).map((_, i) => (
                                <span key={i} className={`w-2.5 h-2.5 rounded-full ${i < usadosMes ? 'bg-sura-blue' : 'bg-gray-200'}`} />
                            ))}
                        </span>
                        {usadosMes >= tope
                            ? `Ya usaste los ${tope} permisos de este mes`
                            : `Te quedan ${tope - usadosMes} de ${tope} permisos este mes`}
                    </span>
                    <span className="text-xs text-gray-400">
                        {mesActual?.ausencias > 0
                            ? `Cuentan tus permisos y las ausencias sin justificar que marca Asistencia (${mesActual.ausencias} este mes).`
                            : 'Cuentan tus permisos y las ausencias sin justificar; no cuentan los de ley ni el descanso compensatorio.'}
                    </span>
                </div>
            )}

            <div className="flex items-center gap-6 border-b border-gray-200 overflow-x-auto">
                <TabButton label="Mis permisos" tab="mis" active={activeTab} onClick={setActiveTab} />
                {porAprobar.length > 0 && (
                    <TabButton label="Por aprobar" tab="aprobar" active={activeTab} onClick={setActiveTab} badge={porAprobar.length} />
                )}
                {isGH && <TabButton label="Por aprobar (GH)" tab="aprobar_gh" active={activeTab} onClick={setActiveTab} badge={porAprobarGH.length} />}
                {isGH && <TabButton label="Todos" tab="todas" active={activeTab} onClick={setActiveTab} />}
                {isGH && <TabButton label="Calendario" tab="calendario" active={activeTab} onClick={setActiveTab} icon={Calendar} />}
                {isGH && <TabButton label="Reglas" tab="reglas" active={activeTab} onClick={setActiveTab} icon={Settings2} />}
            </div>

            {activeTab === 'mis' && (
                <div className="space-y-3">
                    {misSolicitudes.length === 0 ? (
                        <EmptyState message="Todavía no has pedido permisos" sub="Usa Pedir permiso para enviar el primero" />
                    ) : (
                        misSolicitudes.map((s) => (
                            <PermisoFila key={s.id} s={s} tipo={tiposPorClave[s.tipo]} onClick={() => openDetail(s)} />
                        ))
                    )}
                </div>
            )}

            {activeTab === 'aprobar' && (
                <div className="space-y-3">
                    {porAprobar.length === 0 ? (
                        <EmptyState icon={CheckCircle} message="No tienes permisos por aprobar" />
                    ) : (
                        porAprobar.map((s) => (
                            <PermisoFila key={s.id} s={s} tipo={tiposPorClave[s.tipo]} showEmpleado onClick={() => openDetail(s)} />
                        ))
                    )}
                </div>
            )}

            {activeTab === 'aprobar_gh' && isGH && (
                <div className="space-y-3">
                    <p className="text-sm text-gray-500">
                        Llegan aquí los permisos de gerentes y líderes, de quien reporta al GG o al Presidente, de quien no tiene jefe activo y los tipos que aprueba GH.
                    </p>
                    {porAprobarGH.length === 0 ? (
                        <EmptyState icon={CheckCircle} message="No hay permisos esperando a Gestión Humana" />
                    ) : (
                        porAprobarGH.map((s) => (
                            <PermisoFila key={s.id} s={s} tipo={tiposPorClave[s.tipo]} showEmpleado onClick={() => openDetail(s)} />
                        ))
                    )}
                </div>
            )}

            {activeTab === 'todas' && isGH && (
                <div className="space-y-5">
                    <div className="flex flex-wrap justify-end gap-2">
                        <button onClick={() => abrirFormulario(true)} className={botonSecundario}>
                            <UserPlus size={16} /> Cargar permiso de un trabajador
                        </button>
                        <button onClick={handleAnalisisIA} disabled={analizandoIA} className={botonSecundario}>
                            {analizandoIA ? <Loader2 size={16} className="animate-spin" /> : <FaMagic size={13} />}
                            {analizandoIA ? 'Analizando...' : 'Analizar patrones'}
                        </button>
                    </div>
                    {analisisIA && <AnalisisPatronesPanel data={analisisIA} onClose={() => setAnalisisIA(null)} />}
                    <FiltrosGH
                        q={fQ} setQ={setFQ}
                        estado={fEstado} setEstado={setFEstado} estados={ESTADOS_PERMISO}
                        departamento={fDepto} setDepartamento={setFDepto} departamentos={departamentos}
                        desde={fDesde} setDesde={setFDesde} hasta={fHasta} setHasta={setFHasta}
                        onExport={handleExport} exporting={exportando}
                        total={todasSolicitudes.length} mostrados={todasFiltradas.length}
                    />
                    <div className="space-y-3">
                        {todasFiltradas.length === 0 ? (
                            <EmptyState message={todasSolicitudes.length === 0 ? 'No hay permisos registrados' : 'Ninguno coincide con los filtros'} />
                        ) : (
                            todasFiltradas.map((s) => (
                                <PermisoFila key={s.id} s={s} tipo={tiposPorClave[s.tipo]} showEmpleado onClick={() => openDetail(s)} />
                            ))
                        )}
                    </div>
                </div>
            )}

            {activeTab === 'calendario' && isGH && (
                <CalendarioGH
                    year={calYear} month={calMonth}
                    eventosPorDia={calEventosPorDia}
                    departamentos={departamentos} departamentoId={calDepartamentoId}
                    onDepartamentoChange={setCalDepartamentoId}
                    onPrev={() => { if (calMonth === 1) { setCalMonth(12); setCalYear((y) => y - 1); } else setCalMonth((m) => m - 1); }}
                    onNext={() => { if (calMonth === 12) { setCalMonth(1); setCalYear((y) => y + 1); } else setCalMonth((m) => m + 1); }}
                    onToday={() => { setCalYear(new Date().getFullYear()); setCalMonth(new Date().getMonth() + 1); }}
                    renderEvento={(ev, i) => {
                        const cat = tiposPorClave[ev.tipo]?.categoria;
                        return (
                            <div
                                key={i}
                                className={`text-[10px] leading-tight px-1.5 py-0.5 rounded truncate border ${CATEGORIA_COLORES[cat] || CATEGORIA_COLORES.otro}`}
                                title={`${ev.empleado_nombre}: ${ev.tipo_display}${ev.hora_inicio ? ` (${formatHora(ev.hora_inicio)} a ${formatHora(ev.hora_fin)})` : ''}`}
                            >
                                {ev.empleado_nombre?.split(' ')[0]}
                            </div>
                        );
                    }}
                    leyenda={CATEGORIAS_PERMISO.map((c) => (
                        <span key={c.value} className={`inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium border ${CATEGORIA_COLORES[c.value]}`}>
                            {c.label}
                        </span>
                    ))}
                    resumen={calEventos.length > 0 ? `${calEventos.length} permiso(s) aprobado(s) este mes` : null}
                />
            )}

            {activeTab === 'reglas' && isGH && (
                <ReglasPermisos
                    tipos={tiposTodos}
                    reglas={reglas}
                    onCambio={fetchCatalogo}
                />
            )}
        </div>
    );

    // ========================
    // RENDER: Formulario
    // ========================
    const renderCreate = () => {
        const aprob = paraOtro ? aprobacionOtro : aprobacion;
        const tipo = tipoElegido;
        const faltaDato = !formData.tipo || !formData.motivo.trim()
            || (tipo?.categoria === 'otro' && !formData.tipo_otro.trim())
            || (tipo?.categoria === 'compensatorio' && compensables?.aplica && !formData.compensa_fecha)
            || (formData.forma === 'horas' ? !(formData.fecha && formData.hora_inicio && formData.hora_fin) : !formData.fecha_inicio)
            || (formData.forma === 'varios_dias' && !formData.fecha_fin)
            || (paraOtro && !empleadoOtro);
        const bloqueado = faltaDato || calculo.errores.length > 0 || calculando || submitting;
        const unidad = tipo?.dias_continuos ? 'continuos' : 'hábiles';

        let quienAprueba = null;
        if (paraOtro) {
            quienAprueba = 'Lo cargas tú en nombre de Gestión Humana: queda aprobado de una vez.';
        } else if (tipo?.aprueba === 'gh') {
            quienAprueba = `Lo aprueba Gestión Humana: ${tipo.nombre.toLowerCase()} siempre pasa por GH.`;
        } else if (aprob?.gh) {
            quienAprueba = `Lo aprueba Gestión Humana. ${aprob.motivo || ''}`.trim();
        } else if (aprob?.opciones?.length === 1) {
            quienAprueba = `Lo aprueba ${aprob.opciones[0].nombre}${aprob.opciones[0].cargo ? `, ${aprob.opciones[0].cargo}` : ''}.`;
        }

        return (
            <div className="max-w-3xl mx-auto space-y-6">
                <div className="flex items-center gap-3">
                    <button onClick={() => setViewMode('list')} className="p-2 hover:bg-gray-100 rounded-xl transition-colors" aria-label="Volver">
                        <ArrowLeft size={20} />
                    </button>
                    <h2 className="text-2xl font-bold text-gray-900">{paraOtro ? 'Cargar permiso de un trabajador' : 'Pedir permiso'}</h2>
                </div>

                {paraOtro && (
                    <section className="bg-white rounded-2xl border border-gray-200 p-5 space-y-3">
                        <h3 className="text-sm font-semibold text-gray-900">Trabajador</h3>
                        <BuscadorEmpleado value={empleadoOtro} onChange={setEmpleadoOtro} />
                    </section>
                )}

                <section className="bg-white rounded-2xl border border-gray-200 p-5 space-y-4">
                    <h3 className="text-sm font-semibold text-gray-900">Tipo de permiso</h3>
                    {CATEGORIAS_PERMISO.map((cat) => {
                        const deCategoria = tipos.filter((t) => t.categoria === cat.value);
                        if (deCategoria.length === 0) return null;
                        return (
                            <div key={cat.value} className="space-y-2">
                                <p className="text-xs font-medium text-gray-500">{cat.label}</p>
                                <div className="grid sm:grid-cols-2 gap-2" role="radiogroup" aria-label={cat.label}>
                                    {deCategoria.map((t) => {
                                        const activo = formData.tipo === t.clave;
                                        return (
                                            <button
                                                key={t.clave}
                                                type="button"
                                                role="radio"
                                                aria-checked={activo}
                                                onClick={() => elegirTipo(t)}
                                                className={`text-left px-3.5 py-3 rounded-xl border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sura-blue ${
                                                    activo ? 'border-sura-blue bg-sura-blue/5 ring-1 ring-sura-blue' : 'border-gray-200 hover:border-gray-300 bg-white'
                                                }`}
                                            >
                                                <span className={`block text-sm font-medium ${activo ? 'text-sura-blue' : 'text-gray-900'}`}>{t.nombre}</span>
                                                <span className="block text-xs text-gray-500 mt-0.5">{reglasDelTipo(t).join('. ')}</span>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        );
                    })}
                    {tipo?.descripcion && tipo.categoria !== 'otro' && (
                        <p className="text-sm text-gray-600 bg-gray-50 rounded-xl px-3.5 py-2.5">{tipo.descripcion}</p>
                    )}
                    {tipo?.categoria === 'otro' && (
                        <div>
                            <label htmlFor="perm-otro" className="block text-sm font-medium text-gray-700 mb-1.5">¿Qué permiso es?</label>
                            <input
                                id="perm-otro" value={formData.tipo_otro} maxLength={120} autoFocus
                                onChange={(e) => setFormData((prev) => ({ ...prev, tipo_otro: e.target.value }))}
                                placeholder="Por ejemplo: mudanza, trámite en el registro"
                                className={inputClass}
                            />
                        </div>
                    )}
                </section>

                {tipo && (
                    <section className="bg-white rounded-2xl border border-gray-200 p-5 space-y-4">
                        <h3 className="text-sm font-semibold text-gray-900">Cuándo</h3>
                        {formasDelTipo(tipo).length > 1 && (
                            <div className="inline-flex p-1 bg-gray-100 rounded-xl" role="radiogroup" aria-label="Forma del permiso">
                                {formasDelTipo(tipo).map(([valor, etiqueta]) => (
                                    <button
                                        key={valor}
                                        type="button"
                                        role="radio"
                                        aria-checked={formData.forma === valor}
                                        onClick={() => elegirForma(valor)}
                                        className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                                            formData.forma === valor ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                                        }`}
                                    >
                                        {etiqueta}
                                    </button>
                                ))}
                            </div>
                        )}

                        {formData.forma === 'un_dia' && (
                            <div className="sm:w-1/2">
                                <label htmlFor="perm-un-dia" className="block text-sm font-medium text-gray-700 mb-1.5">Día</label>
                                <input
                                    id="perm-un-dia" type="date" value={formData.fecha_inicio}
                                    onChange={(e) => setFormData((prev) => ({ ...prev, fecha_inicio: e.target.value, fecha_fin: e.target.value }))}
                                    className={inputClass}
                                />
                            </div>
                        )}
                        {formData.forma === 'varios_dias' && (
                            <div className="grid sm:grid-cols-2 gap-4">
                                <div>
                                    <label htmlFor="perm-desde" className="block text-sm font-medium text-gray-700 mb-1.5">Desde</label>
                                    <input
                                        id="perm-desde" type="date" value={formData.fecha_inicio}
                                        onChange={(e) => setFormData((prev) => ({
                                            ...prev,
                                            fecha_inicio: e.target.value,
                                            fecha_fin: prev.fecha_fin && prev.fecha_fin < e.target.value ? '' : prev.fecha_fin,
                                        }))}
                                        className={inputClass}
                                    />
                                </div>
                                <div>
                                    <label htmlFor="perm-hasta" className="block text-sm font-medium text-gray-700 mb-1.5">Hasta (último día)</label>
                                    <input
                                        id="perm-hasta" type="date" value={formData.fecha_fin} min={formData.fecha_inicio || undefined}
                                        onChange={(e) => setFormData((prev) => ({ ...prev, fecha_fin: e.target.value }))}
                                        className={inputClass}
                                    />
                                </div>
                            </div>
                        )}
                        {formData.forma === 'horas' && (
                            <div className="grid sm:grid-cols-3 gap-4">
                                <div>
                                    <label htmlFor="perm-dia" className="block text-sm font-medium text-gray-700 mb-1.5">Día</label>
                                    <input
                                        id="perm-dia" type="date" value={formData.fecha}
                                        onChange={(e) => setFormData((prev) => ({ ...prev, fecha: e.target.value }))}
                                        className={inputClass}
                                    />
                                </div>
                                <div>
                                    <label htmlFor="perm-sale" className="block text-sm font-medium text-gray-700 mb-1.5">Sale</label>
                                    <HoraSelect
                                        id="perm-sale" value={formData.hora_inicio}
                                        onChange={(v) => setFormData((prev) => ({ ...prev, hora_inicio: v, hora_fin: prev.hora_fin && prev.hora_fin <= v ? '' : prev.hora_fin }))}
                                    />
                                </div>
                                <div>
                                    <label htmlFor="perm-vuelve" className="block text-sm font-medium text-gray-700 mb-1.5">Vuelve</label>
                                    <HoraSelect
                                        id="perm-vuelve" value={formData.hora_fin} desde={formData.hora_inicio}
                                        onChange={(v) => setFormData((prev) => ({ ...prev, hora_fin: v }))}
                                    />
                                </div>
                            </div>
                        )}

                        {tipo.categoria === 'compensatorio' && diaPermiso && compensables && (
                            <div className="space-y-2">
                                <p className="text-sm font-medium text-gray-700">¿Qué día libre trabajado compensa?</p>
                                {!compensables.aplica ? (
                                    <p className="text-xs text-gray-500">Asistencia no lleva el marcaje de esta persona: Gestión Humana confirma el día que trabajó.</p>
                                ) : compensables.dias.length === 0 ? (
                                    <p className="text-xs text-gray-500">Asistencia no tiene días libres trabajados en los 15 días antes del {formatFecha(diaPermiso)}.</p>
                                ) : (
                                    <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="Día que compensa">
                                        {compensables.dias.map((d) => (
                                            <button
                                                key={d.fecha}
                                                type="button"
                                                role="radio"
                                                aria-checked={formData.compensa_fecha === d.fecha}
                                                disabled={!d.disponible}
                                                onClick={() => setFormData((prev) => ({ ...prev, compensa_fecha: d.fecha }))}
                                                className={`text-left px-3 py-2 rounded-xl border text-sm transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                                                    formData.compensa_fecha === d.fecha ? 'border-sura-blue bg-sura-blue/5 ring-1 ring-sura-blue' : 'border-gray-200 hover:border-gray-300 bg-white'
                                                }`}
                                            >
                                                <span className="block font-medium text-gray-900">{formatFechaDia(d.fecha)}</span>
                                                <span className="block text-xs text-gray-500">
                                                    {d.entrada && d.salida ? `De ${formatHora(d.entrada)} a ${formatHora(d.salida)}. ` : ''}
                                                    {d.disponible ? `Queda ${d.queda}, hasta el ${formatFecha(d.vence)}` : 'Ya compensado'}
                                                </span>
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        <div className="min-h-[1.25rem] text-sm text-gray-600 flex items-center gap-2" aria-live="polite">
                            {calculando && <Loader2 size={14} className="animate-spin text-gray-400" />}
                            {!calculando && formData.forma === 'varios_dias' && calculo.dias != null && (
                                <span>Cuenta como <strong className="text-gray-900">{formatDias(calculo.dias)} {unidad}</strong>.</span>
                            )}
                            {!calculando && formData.forma === 'horas' && calcularDuracionHoras(formData.hora_inicio, formData.hora_fin) && (
                                <span>Ausencia de <strong className="text-gray-900">{calcularDuracionHoras(formData.hora_inicio, formData.hora_fin)}</strong>.</span>
                            )}
                        </div>
                        <AvisosReglas errores={calculo.errores} avisos={calculo.avisos} />

                        {formData.forma !== 'horas' && formData.fecha_inicio && (paraOtro ? empleadoOtro : user?.empleado_id) && (
                            <AvisoSolapamiento
                                empleadoId={paraOtro ? empleadoOtro?.empleado_id : user?.empleado_id}
                                fechaInicio={formData.fecha_inicio}
                                fechaFin={formData.forma === 'un_dia' ? formData.fecha_inicio : (formData.fecha_fin || formData.fecha_inicio)}
                            />
                        )}
                    </section>
                )}

                {tipo && (
                    <section className="bg-white rounded-2xl border border-gray-200 p-5 space-y-4">
                        <div>
                            <label htmlFor="perm-motivo" className="block text-sm font-medium text-gray-700 mb-1.5">Motivo</label>
                            <textarea
                                id="perm-motivo" rows={3} value={formData.motivo}
                                onChange={(e) => setFormData((prev) => ({ ...prev, motivo: e.target.value }))}
                                placeholder="Cuenta brevemente para qué es el permiso"
                                className={`${inputClass} resize-none`}
                            />
                        </div>

                        {tipo.requiere_soporte && (
                            <div className="space-y-2">
                                <p className="text-sm font-medium text-gray-700">Soporte: {tipo.soporte || 'documento'}</p>
                                <p className="text-xs text-gray-500">
                                    Puedes adjuntarlo ahora o hasta {reglas?.soporte_horas || 24} h después de la ausencia.
                                </p>
                                <label className={`${botonSecundario} cursor-pointer w-fit`}>
                                    <Upload size={15} /> Adjuntar archivo
                                    <input
                                        type="file" multiple accept=".pdf,image/*" className="sr-only"
                                        onChange={(e) => { setSoportes((prev) => [...prev, ...Array.from(e.target.files || [])]); e.target.value = ''; }}
                                    />
                                </label>
                                {soportes.length > 0 && (
                                    <ul className="space-y-1">
                                        {soportes.map((f, i) => (
                                            <li key={`${f.name}-${i}`} className="flex items-center gap-2 text-sm text-gray-700">
                                                <Paperclip size={14} className="text-gray-400" />
                                                <span className="truncate">{f.name}</span>
                                                <button
                                                    type="button"
                                                    onClick={() => setSoportes((prev) => prev.filter((_, j) => j !== i))}
                                                    className="p-1 text-gray-400 hover:text-red-500"
                                                    aria-label={`Quitar ${f.name}`}
                                                >
                                                    <X size={14} />
                                                </button>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        )}

                        {!paraOtro && tipo.aprueba !== 'gh' && !aprob?.gh && aprob?.opciones?.length > 1 && (
                            <div>
                                <label htmlFor="perm-jefe" className="block text-sm font-medium text-gray-700 mb-1.5">¿Quién lo aprueba?</label>
                                <select
                                    id="perm-jefe" value={formData.aprobador_id}
                                    onChange={(e) => setFormData((prev) => ({ ...prev, aprobador_id: e.target.value }))}
                                    className={inputClass}
                                >
                                    <option value="">{aprob.opciones[0].nombre} (tu jefe directo)</option>
                                    {aprob.opciones.slice(1).map((o) => (
                                        <option key={o.id} value={o.id}>{o.nombre}{o.cargo ? `, ${o.cargo}` : ''}</option>
                                    ))}
                                </select>
                            </div>
                        )}
                        {quienAprueba && (
                            <p className="flex items-start gap-2 text-sm text-gray-600">
                                <ShieldCheck size={16} className="text-sura-blue mt-0.5 shrink-0" />
                                {quienAprueba}
                            </p>
                        )}
                    </section>
                )}

                <div className="flex justify-end gap-3">
                    <button onClick={() => setViewMode('list')} className={botonSecundario}>Volver</button>
                    <button onClick={handleCrear} disabled={bloqueado} className={botonPrimario}>
                        {submitting ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                        {paraOtro ? 'Cargar permiso' : 'Pedir permiso'}
                    </button>
                </div>
            </div>
        );
    };

    // ========================
    // RENDER: Detalle
    // ========================
    const renderDetail = () => {
        const s = selectedSolicitud;
        if (!s) return null;
        const tipo = tiposPorClave[s.tipo];
        const esMio = s.empleado_id === user?.empleado_id;
        const esJefe = s.aprobador_id && s.aprobador_id === user?.empleado_id;
        const puedeDecidir = s.estado === 'pendiente' && (esJefe || isGH);
        const puedeSubir = (esMio && ['pendiente', 'aprobado'].includes(s.estado)) || isGH;
        const puedeCancelar = (esMio && s.estado === 'pendiente') || (isGH && ['pendiente', 'aprobado'].includes(s.estado));

        return (
            <div className="max-w-3xl mx-auto space-y-6">
                <div className="flex flex-wrap items-center gap-3">
                    <button onClick={() => setViewMode('list')} className="p-2 hover:bg-gray-100 rounded-xl transition-colors" aria-label="Volver">
                        <ArrowLeft size={20} />
                    </button>
                    <div className="flex-1 min-w-0">
                        <h2 className="text-2xl font-bold text-gray-900">{s.tipo_display}</h2>
                        <p className="text-sm text-gray-500">{s.codigo}, pedido el {formatFechaLarga(s.created_at)}</p>
                    </div>
                    <EstadoBadge estado={s.estado} />
                </div>

                <section className="bg-white rounded-2xl border border-gray-200 divide-y divide-gray-100">
                    <dl className="grid sm:grid-cols-2 gap-x-6 gap-y-4 p-5">
                        <Dato label="Trabajador" value={s.empleado_nombre} sub={s.empleado_departamento} />
                        <Dato label="Cuándo" value={cuandoTexto(s)} sub={diasTexto(s, tipo)} />
                        {s.compensa_fecha && <Dato label="Compensa" value={`El día libre trabajado el ${formatFechaDia(s.compensa_fecha)}`} />}
                        {(!esMio || s.estado === 'aprobado') && (
                            <Dato
                                label="Pago"
                                value={s.estado === 'pendiente' ? 'Lo decide quien aprueba' : (s.remunerado ? 'Remunerado' : 'No remunerado')}
                                sub={s.excede_tope ? 'Gestión Humana lo cargó por encima del tope del tipo' : null}
                            />
                        )}
                        <Dato
                            label={s.estado === 'pendiente' ? 'Lo aprueba' : 'Decidió'}
                            value={s.estado === 'pendiente'
                                ? (s.aprueba_gh ? 'Gestión Humana' : s.aprobador_nombre)
                                : (s.aprobado_por_nombre || (s.aprueba_gh ? 'Gestión Humana' : s.aprobador_nombre))}
                            sub={s.estado === 'pendiente' && s.aprueba_gh ? s.motivo_gh : (s.cargado_por_gh ? 'Cargado por Gestión Humana' : null)}
                        />
                        <div className="sm:col-span-2">
                            <Dato label="Motivo" value={s.motivo} />
                        </div>
                        {s.estado === 'rechazado' && s.motivo_rechazo && (
                            <div className="sm:col-span-2">
                                <Dato label="Por qué se rechazó" value={s.motivo_rechazo} />
                            </div>
                        )}
                    </dl>

                    {(s.requiere_soporte || (s.adjuntos && s.adjuntos.length > 0)) && (
                        <div className="p-5 space-y-3">
                            <div className="flex flex-wrap items-center justify-between gap-2">
                                <h3 className="text-sm font-semibold text-gray-900">
                                    Soporte{s.soporte ? `: ${s.soporte.toLowerCase()}` : ''}
                                </h3>
                                {puedeSubir && (
                                    <label className={`${botonSecundario} cursor-pointer ${subiendoSoporte ? 'opacity-50 pointer-events-none' : ''}`}>
                                        {subiendoSoporte ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />}
                                        Subir soporte
                                        <input
                                            type="file" multiple accept=".pdf,image/*" className="sr-only"
                                            onChange={(e) => { handleSubirSoporte(s, Array.from(e.target.files || [])); e.target.value = ''; }}
                                        />
                                    </label>
                                )}
                            </div>
                            {s.falta_soporte && (
                                <p className="flex items-start gap-2 px-3 py-2.5 rounded-xl border border-amber-200 bg-amber-50 text-sm text-amber-900">
                                    <AlertTriangle size={16} className="mt-0.5 shrink-0" />
                                    Falta el soporte. Se entrega hasta {reglas?.soporte_horas || 24} h después de la ausencia.
                                </p>
                            )}
                            {s.adjuntos && s.adjuntos.length > 0 && (
                                <ul className="space-y-1.5">
                                    {s.adjuntos.map((adj) => (
                                        <li key={adj.id} className="flex items-center gap-2 text-sm">
                                            <Paperclip size={14} className="text-gray-400" />
                                            <a
                                                href={withAuthToken(`/api${adj.ruta_archivo}`)} target="_blank" rel="noopener noreferrer"
                                                className="text-sura-blue hover:underline truncate"
                                            >
                                                {adj.nombre_original}
                                            </a>
                                            {esMio && s.estado === 'pendiente' && (
                                                <button
                                                    onClick={() => handleEliminarAdjunto(s, adj)}
                                                    className="p-1 text-gray-400 hover:text-red-500"
                                                    aria-label={`Quitar ${adj.nombre_original}`}
                                                >
                                                    <Trash2 size={14} />
                                                </button>
                                            )}
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    )}
                </section>

                {(esJefe || isGH) && !esMio && (
                    <div className="space-y-3">
                        <ContadorPermisos empleadoId={s.empleado_id} />
                        {s.modalidad === 'dia_completo' && s.estado === 'pendiente' && (
                            <AvisoSolapamiento
                                empleadoId={s.empleado_id}
                                fechaInicio={s.fecha_inicio}
                                fechaFin={s.fecha_fin || s.fecha_inicio}
                                excludePermisoId={s.id}
                            />
                        )}
                    </div>
                )}

                {puedeDecidir && (
                    <section className="bg-white rounded-2xl border border-gray-200 p-5 space-y-4">
                        <div>
                            <h3 className="text-sm font-semibold text-gray-900">Uso del supervisor</h3>
                            <p className="text-xs text-gray-500 mt-0.5">
                                {tipo?.categoria === 'no_programado'
                                    ? 'Este tipo se paga a juicio del líder.'
                                    : 'Viene marcado según el tipo; cámbialo si no corresponde pagarlo.'}
                                {s.tope_mensual > 0 && s.permisos_del_mes !== undefined && (
                                    ` Lleva ${s.permisos_del_mes} ${s.permisos_del_mes === 1 ? 'permiso remunerado' : 'permisos remunerados'}`
                                    + (s.ausencias_del_mes ? ` y ${s.ausencias_del_mes} ${s.ausencias_del_mes === 1 ? 'ausencia' : 'ausencias'} sin justificar` : '')
                                    + ` ese mes; la normativa considera ${s.tope_mensual}.`
                                )}
                            </p>
                        </div>
                        <div className="inline-flex p-1 bg-gray-100 rounded-xl" role="radiogroup" aria-label="Pago del permiso">
                            {[[true, 'Remunerado'], [false, 'No remunerado']].map(([valor, etiqueta]) => (
                                <button
                                    key={etiqueta}
                                    type="button"
                                    role="radio"
                                    aria-checked={remuneradoDecision === valor}
                                    onClick={() => setRemuneradoDecision(valor)}
                                    className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                                        remuneradoDecision === valor ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                                    }`}
                                >
                                    {etiqueta}
                                </button>
                            ))}
                        </div>
                        <div className="flex flex-wrap justify-end gap-3">
                            <button
                                onClick={() => { setSolicitudParaRechazar(s); setShowRechazoModal(true); }}
                                className="px-4 py-2.5 border border-red-200 text-red-600 rounded-xl text-sm font-medium hover:bg-red-50 transition-colors"
                            >
                                Rechazar
                            </button>
                            <button onClick={() => handleAprobar(s)} disabled={submitting} className={botonPrimario}>
                                {submitting ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle size={16} />}
                                Aprobar
                            </button>
                        </div>
                    </section>
                )}

                <div className="flex flex-wrap justify-end gap-3">
                    {puedeCancelar && (
                        <button onClick={() => handleCancelar(s)} className="px-4 py-2.5 text-sm font-medium text-gray-500 hover:text-red-600 transition-colors">
                            {s.estado === 'aprobado' ? 'Anular permiso' : 'Cancelar solicitud'}
                        </button>
                    )}
                    <a
                        href={withAuthToken(`/api/permisos/${s.id}/pdf`)} target="_blank" rel="noopener noreferrer"
                        className={botonSecundario}
                    >
                        <Printer size={16} /> Imprimir formato
                    </a>
                </div>
            </div>
        );
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <Loader2 size={28} className="animate-spin text-gray-400" />
            </div>
        );
    }

    return (
        <div className="max-w-6xl mx-auto px-4 py-6 sm:p-6">
            {viewMode === 'list' && (
                <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900">Permisos</h1>
                        <p className="text-gray-500 mt-1">Pide permisos y sigue su aprobación, con las reglas de la normativa de asistencia.</p>
                    </div>
                    {user?.empleado_id && (
                        <button onClick={() => abrirFormulario(false)} className={botonPrimario}>
                            <Plus size={18} /> Pedir permiso
                        </button>
                    )}
                </div>
            )}

            <AnimatePresence mode="wait">
                <motion.div
                    key={viewMode}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    transition={{ duration: 0.15 }}
                >
                    {viewMode === 'list' && renderList()}
                    {viewMode === 'create' && renderCreate()}
                    {viewMode === 'detail' && renderDetail()}
                </motion.div>
            </AnimatePresence>

            <RechazoModal
                isOpen={showRechazoModal}
                onClose={() => { setShowRechazoModal(false); setMotivoRechazo(''); setSolicitudParaRechazar(null); }}
                onConfirm={handleRechazar}
                solicitud={solicitudParaRechazar}
                motivo={motivoRechazo}
                setMotivo={setMotivoRechazo}
                submitting={submitting}
                titulo="Rechazar permiso"
            />
        </div>
    );
};

// ========================
// Piezas
// ========================
const Dato = ({ label, value, sub }) => (
    <div>
        <dt className="text-xs text-gray-500">{label}</dt>
        <dd className="text-sm font-medium text-gray-900 whitespace-pre-wrap mt-0.5">{value || '-'}</dd>
        {sub && <dd className="text-xs text-gray-500 mt-0.5">{sub}</dd>}
    </div>
);

const PermisoFila = ({ s, tipo, showEmpleado = false, onClick }) => (
    <button
        type="button"
        onClick={onClick}
        className="w-full text-left bg-white rounded-2xl border border-gray-200 px-5 py-4 hover:border-gray-300 transition-colors group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sura-blue"
    >
        <div className="flex items-center gap-4">
            <span className={`hidden sm:block w-1.5 self-stretch rounded-full ${BARRA_CATEGORIA[tipo?.categoria] || 'bg-gray-200'}`} aria-hidden="true" />
            <div className="flex-1 min-w-0 space-y-1">
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                    <span className="text-sm font-semibold text-gray-900">
                        {showEmpleado ? s.empleado_nombre : s.tipo_display}
                    </span>
                    <EstadoBadge estado={s.estado} />
                    {s.falta_soporte && ['pendiente', 'aprobado'].includes(s.estado) && (
                        <span className="inline-flex items-center gap-1 text-xs text-amber-700">
                            <AlertTriangle size={12} /> Falta soporte
                        </span>
                    )}
                    {!s.remunerado && s.estado === 'aprobado' && <span className="text-xs text-gray-500">No remunerado</span>}
                </div>
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-500">
                    {showEmpleado && (
                        <span className="flex items-center gap-1.5"><FileText size={14} />{s.tipo_display}</span>
                    )}
                    <span className="flex items-center gap-1.5">
                        {s.modalidad === 'dia_completo' ? <Calendar size={14} /> : <Clock size={14} />}
                        {cuandoTexto(s)}
                    </span>
                    {s.estado === 'pendiente' && (
                        <span className="flex items-center gap-1.5">
                            <User size={14} />
                            {s.aprueba_gh ? 'Espera a Gestión Humana' : `Espera a ${s.aprobador_nombre}`}
                        </span>
                    )}
                    {s.adjuntos && s.adjuntos.length > 0 && (
                        <span className="flex items-center gap-1"><Paperclip size={14} />{s.adjuntos.length}</span>
                    )}
                </div>
            </div>
            <span className="text-xs text-gray-400 hidden sm:block">{s.codigo}</span>
            <ChevronRight size={18} className="text-gray-300 group-hover:text-gray-500 transition-colors" />
        </div>
    </button>
);

// Buscador de trabajadores activos (GH). Usa la lista de saldos de vacaciones, que ya trae a todos.
const BuscadorEmpleado = ({ value, onChange }) => {
    const [lista, setLista] = useState([]);
    const [q, setQ] = useState('');
    const [cargando, setCargando] = useState(true);
    const inputRef = useRef(null);

    useEffect(() => {
        let activo = true;
        axiosInstance.get('/api/vacaciones/saldos')
            .then((res) => { if (activo) setLista(res.data); })
            .catch(() => { if (activo) toast.error('No se pudo cargar la lista de trabajadores'); })
            .finally(() => { if (activo) setCargando(false); });
        return () => { activo = false; };
    }, []);

    const resultados = useMemo(() => {
        const consulta = normalizarTexto(q).trim();
        if (!consulta) return [];
        return lista.filter((e) => normalizarTexto(`${e.nombre} ${e.ficha || ''} ${e.departamento || ''}`).includes(consulta)).slice(0, 8);
    }, [q, lista]);

    if (value) {
        return (
            <div className="flex items-center justify-between gap-3 px-3.5 py-3 rounded-xl border border-gray-200 bg-gray-50">
                <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{value.nombre}</p>
                    <p className="text-xs text-gray-500 truncate">Ficha {value.ficha}{value.cargo ? `, ${value.cargo}` : ''}</p>
                </div>
                <button
                    type="button"
                    onClick={() => { onChange(null); setQ(''); setTimeout(() => inputRef.current?.focus(), 0); }}
                    className="text-sm text-sura-blue hover:underline shrink-0"
                >
                    Cambiar
                </button>
            </div>
        );
    }

    return (
        <div className="space-y-2">
            <div className="relative">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                    ref={inputRef}
                    type="search" value={q} onChange={(e) => setQ(e.target.value)}
                    placeholder={cargando ? 'Cargando trabajadores...' : 'Busca por nombre, ficha o departamento'}
                    aria-label="Buscar trabajador"
                    className={`${inputClass} pl-9`}
                />
            </div>
            {resultados.length > 0 && (
                <ul className="border border-gray-200 rounded-xl divide-y divide-gray-100 overflow-hidden">
                    {resultados.map((e) => (
                        <li key={e.empleado_id}>
                            <button
                                type="button"
                                onClick={() => onChange(e)}
                                className="w-full text-left px-3.5 py-2.5 hover:bg-gray-50 focus-visible:bg-gray-50 focus-visible:outline-none"
                            >
                                <span className="block text-sm font-medium text-gray-900">{e.nombre}</span>
                                <span className="block text-xs text-gray-500">Ficha {e.ficha}{e.departamento ? `, ${e.departamento}` : ''}</span>
                            </button>
                        </li>
                    ))}
                </ul>
            )}
            {q.trim() && !cargando && resultados.length === 0 && (
                <p className="text-sm text-gray-500">Nadie coincide con esa búsqueda.</p>
            )}
        </div>
    );
};

// ========================
// Reglas (GH): catálogo de tipos y reglas generales
// ========================
const ReglasPermisos = ({ tipos, reglas, onCambio }) => {
    const [generales, setGenerales] = useState(reglas || {});
    const [guardando, setGuardando] = useState(false);
    const [editando, setEditando] = useState(null);   // tipo a editar, o TIPO_VACIO para uno nuevo

    useEffect(() => { setGenerales(reglas || {}); }, [reglas]);

    const guardarGenerales = async () => {
        try {
            setGuardando(true);
            await axiosInstance.put('/api/permisos/reglas', {
                tope_mensual: Number(generales.tope_mensual),
                medio_dia_horas: Number(generales.medio_dia_horas),
                soporte_horas: Number(generales.soporte_horas),
            });
            toast.success('Reglas guardadas');
            onCambio();
        } catch (err) {
            toast.error(err.response?.data?.error || 'No se pudieron guardar las reglas');
        } finally {
            setGuardando(false);
        }
    };

    return (
        <div className="space-y-6">
            <section className="bg-white rounded-2xl border border-gray-200 p-5 space-y-4">
                <div>
                    <h3 className="text-base font-semibold text-gray-900">Reglas generales</h3>
                    <p className="text-sm text-gray-500">De la Normativa para Control de Asistencia del Personal (rev. 3, 2025).</p>
                </div>
                <div className="grid sm:grid-cols-3 gap-4">
                    <div>
                        <label htmlFor="r-tope" className="block text-sm font-medium text-gray-700 mb-1.5">Permisos remunerados al mes</label>
                        <input
                            id="r-tope" type="number" min="0" max="31" value={generales.tope_mensual ?? ''}
                            onChange={(e) => setGenerales((g) => ({ ...g, tope_mensual: e.target.value }))}
                            className={inputClass}
                        />
                        <p className="text-xs text-gray-400 mt-1">0 quita el tope.</p>
                    </div>
                    <div>
                        <label htmlFor="r-medio" className="block text-sm font-medium text-gray-700 mb-1.5">Horas de medio día</label>
                        <input
                            id="r-medio" type="number" min="1" max="12" step="0.5" value={generales.medio_dia_horas ?? ''}
                            onChange={(e) => setGenerales((g) => ({ ...g, medio_dia_horas: e.target.value }))}
                            className={inputClass}
                        />
                        <p className="text-xs text-gray-400 mt-1">De 8:00 a. m. a 12:30 p. m. son 4,5.</p>
                    </div>
                    <div>
                        <label htmlFor="r-soporte" className="block text-sm font-medium text-gray-700 mb-1.5">Plazo del soporte (horas)</label>
                        <input
                            id="r-soporte" type="number" min="0" value={generales.soporte_horas ?? ''}
                            onChange={(e) => setGenerales((g) => ({ ...g, soporte_horas: e.target.value }))}
                            className={inputClass}
                        />
                        <p className="text-xs text-gray-400 mt-1">Después de la ausencia.</p>
                    </div>
                </div>
                <div className="flex justify-end">
                    <button onClick={guardarGenerales} disabled={guardando} className={botonPrimario}>
                        {guardando ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                        Guardar reglas
                    </button>
                </div>
            </section>

            <section className="bg-white rounded-2xl border border-gray-200 p-5 space-y-5">
                <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                        <h3 className="text-base font-semibold text-gray-900">Tipos de permiso</h3>
                        <p className="text-sm text-gray-500">Lo que el trabajador ve al pedir. Los tipos no se borran: se desactivan.</p>
                    </div>
                    <button onClick={() => setEditando(TIPO_VACIO)} className={botonSecundario}>
                        <Plus size={16} /> Agregar tipo
                    </button>
                </div>
                {CATEGORIAS_PERMISO.map((cat) => {
                    const deCategoria = tipos.filter((t) => t.categoria === cat.value);
                    if (deCategoria.length === 0) return null;
                    return (
                        <div key={cat.value}>
                            <p className="text-xs font-medium text-gray-500 mb-2">{cat.label}</p>
                            <ul className="border border-gray-200 rounded-xl divide-y divide-gray-100">
                                {deCategoria.map((t) => (
                                    <li key={t.id} className={`flex items-center gap-3 px-4 py-3 ${t.activo ? '' : 'bg-gray-50'}`}>
                                        <div className="flex-1 min-w-0">
                                            <p className={`text-sm font-medium ${t.activo ? 'text-gray-900' : 'text-gray-400'}`}>
                                                {t.nombre}{!t.activo && ' (inactivo)'}
                                            </p>
                                            <p className="text-xs text-gray-500">
                                                {[
                                                    ...reglasDelTipo(t, true),
                                                    t.permite_horas && t.permite_dias ? 'Por horas o por días' : (t.permite_horas ? 'Por horas' : 'Por días'),
                                                    t.cuenta_tope_mensual ? 'Cuenta en el tope mensual' : null,
                                                ].filter(Boolean).join('. ')}
                                            </p>
                                        </div>
                                        <button
                                            onClick={() => setEditando({ ...t, tope_dias: t.tope_dias ?? '', soporte: t.soporte || '', descripcion: t.descripcion || '' })}
                                            className="p-2 text-gray-400 hover:text-sura-blue hover:bg-gray-100 rounded-lg transition-colors"
                                            aria-label={`Editar ${t.nombre}`}
                                        >
                                            <Pencil size={16} />
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    );
                })}
            </section>

            {editando && (
                <TipoModal
                    tipo={editando}
                    onClose={() => setEditando(null)}
                    onGuardado={() => { setEditando(null); onCambio(); }}
                />
            )}
        </div>
    );
};

const TipoModal = ({ tipo, onClose, onGuardado }) => {
    const [t, setT] = useState(tipo);
    const [guardando, setGuardando] = useState(false);
    const nuevo = !tipo.id;
    const set = (campo) => (valor) => setT((prev) => ({ ...prev, [campo]: valor }));

    const guardar = async () => {
        try {
            setGuardando(true);
            const payload = { ...t, tope_dias: t.tope_dias === '' ? null : Number(t.tope_dias) };
            if (nuevo) await axiosInstance.post('/api/permisos/tipos', payload);
            else await axiosInstance.put(`/api/permisos/tipos/${t.id}`, payload);
            toast.success(nuevo ? 'Tipo agregado' : 'Tipo guardado');
            onGuardado();
        } catch (err) {
            toast.error(err.response?.data?.error || 'No se pudo guardar el tipo');
        } finally {
            setGuardando(false);
        }
    };

    return (
        <Modal isOpen onClose={onClose} size="lg">
            <Modal.Header onClose={onClose}>{nuevo ? 'Agregar tipo de permiso' : `Editar ${tipo.nombre}`}</Modal.Header>
            <Modal.Body className="space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                        <label htmlFor="t-nombre" className="block text-sm font-medium text-gray-700 mb-1.5">Nombre</label>
                        <input id="t-nombre" value={t.nombre} onChange={(e) => set('nombre')(e.target.value)} className={inputClass} />
                    </div>
                    <div>
                        <label htmlFor="t-cat" className="block text-sm font-medium text-gray-700 mb-1.5">Categoría</label>
                        <select id="t-cat" value={t.categoria} onChange={(e) => set('categoria')(e.target.value)} className={inputClass}>
                            {CATEGORIAS_PERMISO.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                        </select>
                    </div>
                </div>
                <div>
                    <label htmlFor="t-desc" className="block text-sm font-medium text-gray-700 mb-1.5">Explicación para el trabajador</label>
                    <input id="t-desc" value={t.descripcion} onChange={(e) => set('descripcion')(e.target.value)} className={inputClass} placeholder="Ej.: padres, cónyuge, hijos o hermanos" />
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                        <label htmlFor="t-tope" className="block text-sm font-medium text-gray-700 mb-1.5">Tope por permiso (días)</label>
                        <input
                            id="t-tope" type="number" min="0.5" step="0.5" value={t.tope_dias}
                            onChange={(e) => set('tope_dias')(e.target.value)} className={inputClass} placeholder="Sin tope"
                        />
                        <p className="text-xs text-gray-400 mt-1">0,5 es medio día (solo por horas). Vacío, sin tope.</p>
                    </div>
                    <div>
                        <label htmlFor="t-aprueba" className="block text-sm font-medium text-gray-700 mb-1.5">Lo aprueba</label>
                        <select id="t-aprueba" value={t.aprueba} onChange={(e) => set('aprueba')(e.target.value)} className={inputClass}>
                            <option value="jefe">El jefe (líder)</option>
                            <option value="gh">Gestión Humana</option>
                        </select>
                    </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-3">
                    <Casilla checked={t.permite_horas} onChange={set('permite_horas')} label="Se puede pedir por horas" />
                    <Casilla checked={t.permite_dias} onChange={set('permite_dias')} label="Se puede pedir por días" />
                    <Casilla checked={t.dias_continuos} onChange={set('dias_continuos')} label="Cuenta días continuos" hint="Si no, solo los días que la persona trabaja." />
                    <Casilla checked={t.remunerado} onChange={set('remunerado')} label="Remunerado" hint="El jefe lo puede cambiar al aprobar." />
                    <Casilla checked={t.cuenta_tope_mensual} onChange={set('cuenta_tope_mensual')} label="Cuenta en el tope mensual" />
                    <Casilla checked={t.activo} onChange={set('activo')} label="Activo" hint="Si no, deja de salir al pedir." />
                </div>
                <div className="space-y-2">
                    <Casilla checked={t.requiere_soporte} onChange={set('requiere_soporte')} label="Pide soporte" />
                    {t.requiere_soporte && (
                        <input
                            value={t.soporte} onChange={(e) => set('soporte')(e.target.value)}
                            className={inputClass} placeholder="Qué soporte: Acta de matrimonio, constancia de la cita..." aria-label="Qué soporte pide"
                        />
                    )}
                </div>
            </Modal.Body>
            <Modal.Footer>
                <button onClick={onClose} className={botonSecundario}>Volver</button>
                <button onClick={guardar} disabled={guardando || !t.nombre.trim()} className={botonPrimario}>
                    {guardando ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                    {nuevo ? 'Agregar tipo' : 'Guardar tipo'}
                </button>
            </Modal.Footer>
        </Modal>
    );
};

// ========================
// Panel de análisis IA
// ========================
const SEVERIDAD_COLORES = {
    alta: 'bg-red-50 border-red-200 text-red-700',
    media: 'bg-amber-50 border-amber-200 text-amber-700',
    baja: 'bg-blue-50 border-blue-200 text-blue-700',
};

export const AnalisisPatronesPanel = ({ data, onClose }) => (
    <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
            <div className="flex items-center gap-3">
                <FaMagic size={16} className="text-violet-600" />
                <div>
                    <h3 className="font-semibold text-gray-900">Patrones de ausencia</h3>
                    <p className="text-xs text-gray-500">Análisis generado con IA sobre los permisos aprobados del último año</p>
                </div>
            </div>
            <button onClick={onClose} className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors" aria-label="Cerrar análisis">
                <X size={18} />
            </button>
        </div>

        <div className="p-5 space-y-6">
            <p className="text-gray-700 leading-relaxed">{data.resumen}</p>

            {data.estadisticas && (
                <dl className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {[
                        ['Tipo más frecuente', data.estadisticas.tipo_mas_frecuente],
                        ['Día con más ausencias', data.estadisticas.dia_semana_mas_ausencias],
                        ['Departamento con más ausencias', data.estadisticas.departamento_mas_ausencias],
                        ['Promedio mensual', data.estadisticas.promedio_mensual],
                    ].filter(([, v]) => v !== null && v !== undefined && v !== '').map(([k, v]) => (
                        <div key={k} className="bg-gray-50 rounded-xl p-3">
                            <dt className="text-xs text-gray-500">{k}</dt>
                            <dd className="text-sm font-semibold text-gray-900 mt-1">{v}</dd>
                        </div>
                    ))}
                </dl>
            )}

            {data.patrones && data.patrones.length > 0 && (
                <div>
                    <h4 className="text-sm font-semibold text-gray-900 mb-3">Patrones detectados</h4>
                    <div className="space-y-2">
                        {data.patrones.map((p, i) => (
                            <div key={i} className={`p-4 rounded-xl border ${SEVERIDAD_COLORES[p.severidad] || SEVERIDAD_COLORES.baja}`}>
                                <div className="flex items-center gap-2 mb-1">
                                    <span className="font-medium text-sm">{p.titulo}</span>
                                    <span className="text-xs opacity-70">Severidad {p.severidad}</span>
                                </div>
                                <p className="text-sm opacity-80">{p.detalle}</p>
                                {p.empleados_involucrados && p.empleados_involucrados.length > 0 && (
                                    <p className="text-xs mt-2 opacity-60">Involucra a: {p.empleados_involucrados.join(', ')}</p>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {data.tendencias && data.tendencias.length > 0 && (
                <div>
                    <h4 className="text-sm font-semibold text-gray-900 mb-3">Tendencias</h4>
                    <div className="space-y-2">
                        {data.tendencias.map((t, i) => (
                            <div key={i} className="p-3 bg-gray-50 rounded-xl">
                                <p className="text-sm font-medium text-gray-800">{t.titulo}</p>
                                <p className="text-sm text-gray-500 mt-0.5">{t.detalle}</p>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {data.recomendaciones && data.recomendaciones.length > 0 && (
                <div>
                    <h4 className="text-sm font-semibold text-gray-900 mb-3">Recomendaciones</h4>
                    <ul className="space-y-2">
                        {data.recomendaciones.map((r, i) => (
                            <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                                <CheckCircle size={16} className="text-emerald-500 mt-0.5 flex-shrink-0" />
                                {r}
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    </div>
);

export default PermisosView;
